import deepmerge from "deepmerge";
// @ts-ignore
import { isPlainObject } from "is-plain-object";
import { KonturColors } from "@skbkontur/colors";

import { arrayHasLength } from "../../../../../../../Common/TypingUtils";
import type { DeepPartial } from "../../../../../../../Common/TypingUtils";
import {
    reactUiModalBottomPadding,
    reactUiModalLeftPadding,
    reactUiModalRightPadding,
} from "../../ReactUiModalPaddings";
import { Length } from "../Utils/CssLength";
import { themeObject } from "../Utils/ThemeObject";

import { ICandyTheme, ICandyThemeTypography, sizeToken } from "./ICandyTheme";
import { IThemeTokens } from "./ThemeTokens";

export function overrideTheme(source: ICandyTheme, override: DeepPartial<ICandyTheme>): ICandyTheme {
    // @ts-expect-error Тупая там дефиниция
    return deepmerge(source, override, {
        isMergeableObject: value => {
            if (value instanceof Length) {
                return false;
            }
            return isPlainObject(value);
        },
    });
}

export function buildTheme(tokens: IThemeTokens): ICandyTheme {
    const blackAndWhitePalette = tokens.colors.blackAndWhite;
    const brandColors = tokens.colors.brand;
    const semanticColors = tokens.colors.semantic;

    const typography: ICandyThemeTypography = {
        link: {
            color: semanticColors.action.color,
            inactiveColor: blackAndWhitePalette.inactiveTextColor,
        },
        fontFamily: {
            regular: "Lab Grotesque, Segoe UI, Arial, sans-serif",
            monospace: "Consolas, monospace",
        },
        secondary: themeObject({ color: blackAndWhitePalette.secondaryTextColor }),
        inactive: themeObject({ color: blackAndWhitePalette.inactiveTextColor }),
        smallFont: themeObject({
            color: blackAndWhitePalette.secondaryTextColor,
            fontSize: Length.px(12),
            lineHeight: Length.px(15),
            fontWeight: 400,
        }),
        getEstimatedTextWidthInPxForPrimaryFont: (text: string) => {
            const charLength = 7.5;
            return text.length * charLength;
        },
        primary: themeObject({
            fontSize: Length.px(14),
            color: blackAndWhitePalette.primaryTextColor,
            lineHeight: Length.px(20),
        }),
        header2: themeObject({
            fontSize: Length.px(24),
            lineHeight: Length.px(32),
            fontWeight: 400,
        }),
        header3: themeObject({
            fontSize: Length.px(18),
            lineHeight: Length.px(28),
            fontWeight: 700,
        }),
        header4: themeObject({
            fontSize: Length.px(16),
            lineHeight: Length.px(24),
            fontWeight: 500,
        }),
        subheader: {
            color: {
                link: semanticColors.action.color,
                primary: KonturColors.grayscaleText,
                secondary: blackAndWhitePalette.secondaryTextColor,
            },
        },
    };

    const invertedColors = {
        backgroundColor: KonturColors.grayscaleBlack000,
        hoveredColor: KonturColors.grayscaleText,
        color: KonturColors.grayscaleWhiteFFF,
        inactiveColor: KonturColors.grayscaleSecondaryText,
    };

    const cardActualInfo = {
        backgroundLightGreenColor: KonturColors.mainSuccessBackgroundGreen10,
        backgroundDarkGreenColor: KonturColors.green80,
        linkColor: KonturColors.mainLinkTextBlue90,
        backgroundRedColor: KonturColors.mainErrorBackgroundRed10,
    };

    return {
        rootColorScheme: undefined,
        size: size,
        typography: typography,
        loadingOverlayBackgroundColor: KonturColors.grayscale16DarkBackground,
        pageLayout: {
            sidebar: {
                width: Length.px(250),
                wideModeWidth: Length.px(450),
            },
        },
        keyPalette: {
            primaryBackgroundColor: blackAndWhitePalette.primaryBackgroundColor,
            panelBackgroundColor: blackAndWhitePalette.panelBackgroundColor,
            panel2BackgroundColor: blackAndWhitePalette.panel2BackgroundColor,
            panel3BackgroundColor: blackAndWhitePalette.panel3BackgroundColor,
            panel2BackgroundColorAsBlackTransparent: blackAndWhitePalette.panel2BackgroundColorAsBlackTransparent,
        },
        modals: {
            headerFont: typography.header2,
        },
        dropdown: {
            arrowIconColor: blackAndWhitePalette.inactiveTextColor,
        },
        menu: {
            hoveredBackgroundColor: blackAndWhitePalette.panel2BackgroundColorAsBlackTransparent,
        },
        invertedColors: invertedColors,
        brandColors: brandColors,
        semanticColors: semanticColors,
        icons: {
            color: blackAndWhitePalette.secondaryTextColor,
            disabledColor: blackAndWhitePalette.inactiveTextColor,
            hoverColor: blackAndWhitePalette.primaryTextColor,
            hoverBackgroundColor: blackAndWhitePalette.panel2BackgroundColor,
        },
        document: {
            bodyBackgroundColor: blackAndWhitePalette.panelBackgroundColor,
        },
        lists: {
            dividerColor: blackAndWhitePalette.dividerColor,
            items: {
                default: {
                    backgroundColor: undefined,
                    secondaryColor: blackAndWhitePalette.panelSecondaryColor,
                },
                accentLow: {
                    backgroundColor: blackAndWhitePalette.panel2BackgroundColor,
                },
                accentHigh: {
                    backgroundColor: blackAndWhitePalette.primaryBackgroundColor,
                },
            },
        },
        border: {
            color: blackAndWhitePalette.dividerColor,
            solid: `1px solid ${blackAndWhitePalette.dividerColor}`,
            dashed: `1px dashed ${blackAndWhitePalette.dividerColor}`,
        },
        valueControls: {
            highlightedBorderColor: semanticColors.action.alternativeColor,
            disabledCursorTextShadowColor: KonturColors.grayscaleBlack000,
            blinkColor: KonturColors.shade16Black,
            focusedInsetColor: KonturColors.grayscaleWhiteFFF,
            placeholderColor: KonturColors.grayscaleSecondaryText,
        },
        tables: {
            headers: {
                smallFont: typography.smallFont,
            },
            border: {
                color: blackAndWhitePalette.dividerColor,
                solid: `1px solid ${blackAndWhitePalette.dividerColor}`,
            },
        },
        graphics: {
            light: {
                strokeColor: blackAndWhitePalette.dividerColor,
                fillColor: blackAndWhitePalette.primaryBackgroundColor,
            },
            dark: {
                strokeColor: blackAndWhitePalette.primaryBackgroundColor,
                fillColor: blackAndWhitePalette.inactiveTextColor,
            },
        },
        legacy: {
            scrollBarBackgroundColor: "rgba(183, 183, 183, 0.7)",
            shadowColor: KonturColors.grayscaleBackground,
        },
        reactUISizes: {
            input: { height: Length.px(34) },
            modal: {
                rightPadding: Length.px(reactUiModalRightPadding),
                leftPadding: Length.px(reactUiModalLeftPadding),
                bottomPadding: Length.px(reactUiModalBottomPadding),
            },
            button: {
                paddingLeft: Length.px(15),
                paddingRight: Length.px(15),
            },
        },
        patchFufCombineMethods: {
            border: KonturColors.grayscaleBackground,
            hoveredBackgroundColor: KonturColors.grayscaleLightBackground,
        },
        banner: {
            light: {
                color: KonturColors.grayscaleText,
                backgroundColor: KonturColors.grayscaleWhiteFFF,
                closeCrossIconColor: KonturColors.grayscaleDisabledText,
            },
            dark: {
                color: KonturColors.grayscaleWhiteFFF,
                backgroundColor: KonturColors.orangePromoAccent,
                closeCrossIconColor: KonturColors.grayscaleLightBackground,
            },
        },
        cardActualInfo: cardActualInfo,
    };
}

function size(value: number): number;
function size(value: number | string | Array<string | number>): number | string;
function size(value: number | string | Array<string | number>): number | string {
    if (Array.isArray(value)) {
        if (arrayHasLength(value, 1)) {
            return size(value[0]);
        }
        return value
            .map(x => size(x))
            .map(x => x.toString())
            .join(" ");
    }
    if (value === 0) {
        return 0;
    } else if (typeof value === "string") {
        return value;
    } else {
        return `${sizeToken(value)}px`;
    }
}
